import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { FingerPrintService } from 'src/app/shared/bc-connect/services/finger-print.service';
import { DisbursementSessionRequest, DisbursementResponse, DisbursementStatusResponse } from 'src/app/shared/bc-connect/interfaces/disbursement.interface';
import { ConnectMessagePageData } from 'src/app/shared/bc-connect/interfaces/connect-message-page-model.interface';
import { CREDIT_DENIED_MESSAGE } from 'src/app/shared/bc-connect/constants/connect-message-page-data';

@Injectable({
  providedIn: 'root',
})
export class BancolombiaServiceService {

  private readonly fingerPrintService = inject(FingerPrintService);
  public validateResponse: any = null;
  public fuaPopupRef: Window | null = null;
  public fuaConfirmed = signal(false);
  public fuaPopupClosed = signal(false);
  public acceptedTerm = signal(false);
  public termsUrl = signal<string>('');
  public validateTrigger = signal(0);
  public validateError = signal(false);
  public creditDenied = signal(false);
  public responsePreApproved = signal<any>(null);
  public accountsResponse = signal<any>(null);
  public orderNumber = signal<string>('');
  public currentFlowName: 'Crédito pre aprobado' | 'Evaluación de crédito' = 'Crédito pre aprobado';

  public simulationResults = signal<{ planBasico: any; planPlus: any } | null>(null);
  public selectedPlanType = signal<'basic' | 'plus' | null>(null);

  public creditDeniedMessage: ConnectMessagePageData = CREDIT_DENIED_MESSAGE;

  public simulationForm = new FormGroup({
    monto: new FormControl<string>('', Validators.required),
    plazo: new FormControl<string>('', Validators.required),
    plan: new FormControl<number | null>(null, Validators.required),
  });

  public depositAccountForm = new FormGroup({
    depositAccount: new FormControl<any>('', Validators.required),
    paymentDay: new FormControl<string>('', Validators.required),
  });

  public personalInfoForm = new FormGroup({
    genero: new FormControl<'F' | 'M' | ''>('', Validators.required),
    estadoCivil: new FormControl<string>('', Validators.required),
    personasACargo: new FormControl<string>('', [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(10),
      Validators.pattern(/^\d+$/),
    ]),
    tipoVivienda: new FormControl<string>('', Validators.required),
    ocupacion: new FormControl<string>('', Validators.required),
    tipoContrato: new FormControl<string>('', Validators.required),
    mesesContrato: new FormControl<string>(''),
    programa: new FormControl<string>(''),
    nivelAcademico: new FormControl<string>('', Validators.required),
  });

  public financialInfoForm = new FormGroup({
    ingresosFijosMensuales: new FormControl<string>('', [Validators.required, Validators.pattern(/^\d+$/), Validators.minLength(1), Validators.maxLength(27)]),
    tieneIngresosAdicionales: new FormControl<'SI' | 'NO' | ''>('', Validators.required),
    ingresosAdicionalesMensuales: new FormControl<string>('', [Validators.pattern(/^\d*$/), Validators.minLength(1), Validators.maxLength(27)]),
    ingresosExterior: new FormControl<string>('', [Validators.pattern(/^\d*$/), Validators.minLength(1), Validators.maxLength(27)]),
    ingresosRentaHonorarios: new FormControl<string>('', [Validators.pattern(/^\d*$/), Validators.minLength(1), Validators.maxLength(27)]),
    totalGastosMensuales: new FormControl<string>('', [Validators.required, Validators.pattern(/^\d+$/), Validators.minLength(1), Validators.maxLength(27)]),
    tieneBienesRaices: new FormControl<'SI' | 'NO' | ''>('', Validators.required),
    valorBienesRaices: new FormControl<string>('', [Validators.pattern(/^\d*$/), Validators.minLength(1), Validators.maxLength(27)]),
    valorTotalActivos: new FormControl<string>('', [Validators.required, Validators.pattern(/^\d+$/), Validators.minLength(1), Validators.maxLength(27)]),
  });

  constructor(
    public http: HttpClient,
  ) { }

  resetFlowData(): void {
    this.simulationForm.reset({ monto: '', plazo: '', plan: null });
    this.currentFlowName = 'Crédito pre aprobado';
    this.simulationResults.set(null);
    this.selectedPlanType.set(null);
    this.responsePreApproved.set(null);
    this.accountsResponse.set(null);
    this.termsUrl.set('');
    this.orderNumber.set('');
    this.acceptedTerm.set(false);
    this.depositAccountForm.reset({ depositAccount: '', paymentDay: '' });
    this.personalInfoForm.reset({
      genero: '', estadoCivil: '', personasACargo: '', tipoVivienda: '',
      ocupacion: '', tipoContrato: '', mesesContrato: '', programa: '', nivelAcademico: '',
    });
    this.financialInfoForm.reset({
      ingresosFijosMensuales: '', tieneIngresosAdicionales: '', ingresosAdicionalesMensuales: '',
      ingresosExterior: '', ingresosRentaHonorarios: '', totalGastosMensuales: '',
      tieneBienesRaices: '', valorBienesRaices: '', valorTotalActivos: '',
    });
  }

  /** Centraliza el armado de headers (fingerprint) y el POST a los endpoints de Bancolombia */
  private postGenericBancolombia<T>(endpoint: string, body: any, methodName: string): Observable<T> {
    return this.fingerPrintService.getClientRequestHeaders().pipe(
      switchMap(({ clientIpAddress, deviceId }) => {
        const headers = new HttpHeaders({
          ...environment.headersBancolombia,
          'client-ip-address': clientIpAddress,
          'device-id': deviceId,
        });
        return this.http.post<T>(environment.APIEndpoint_vpce_ws + endpoint, body, { headers });
      }),
      catchError(error => {
        console.error(`Error en ${methodName}:`, error);
        throw error;
      })
    );
  }

  customersValidate(body: any): Observable<any> {
    return this.postGenericBancolombia(environment.conectaBancolombia.customersValidate, body, 'customersValidate');
  }

  customersAuthenticate(body: any): Observable<any> {
    return this.postGenericBancolombia(environment.conectaBancolombia.customersAuthenticate, body, 'customersAuthenticate');
  }

  termsRetrieve(body: any): Observable<any> {
    return this.postGenericBancolombia(environment.conectaBancolombia.termsRetrieve, body, 'termsRetrieve');
  }

  termsRegister(body: any): Observable<any> {
    return this.postGenericBancolombia(environment.conectaBancolombia.termsRegister, body, 'termsRegister');
  }

  decline(body: any): Observable<any> {
    return this.postGenericBancolombia(environment.conectaBancolombia.decline, body, 'decline');
  }

  enableOffersPreapproved(body: any): Observable<any> {
    return this.postGenericBancolombia(environment.conectaBancolombia.enableOffersPreapproved, body, 'enableOffersPreapproved');
  }

  simulations(body: any): Observable<any> {
    return this.postGenericBancolombia(environment.conectaBancolombia.simulations, body, 'simulations');
  }

  accountsRetrieve(body: any): Observable<any> {
    return this.postGenericBancolombia(environment.conectaBancolombia.accountsRetrieve, body, 'accountsRetrieve');
  }

  disbursementsConfirm(body: any): Observable<any> {
    return this.postGenericBancolombia(environment.conectaBancolombia.disbursementsConfirm, body, 'disbursementsConfirm');
  }

  customersSignDocument(body: any): Observable<any> {
    return this.postGenericBancolombia(environment.conectaBancolombia.customersSignDocument, body, 'customersSignDocument');
  }

  customersSigningCode(body: any): Observable<any> {
    return this.postGenericBancolombia(environment.conectaBancolombia.customersSigningCode, body, 'customersSigningCode');
  }

  disbursements(body: DisbursementSessionRequest): Observable<DisbursementResponse> {
    return this.postGenericBancolombia(environment.conectaBancolombia.disbursements, body, 'disbursements');
  }

  disbursementsStatus(body: DisbursementSessionRequest): Observable<DisbursementStatusResponse> {
    return this.postGenericBancolombia(environment.conectaBancolombia.disbursementsStatus, body, 'disbursementsStatus');
  }

  validateCreditStudy(body: any): Observable<any> {
    return this.postGenericBancolombia(environment.conectaBancolombia.validateCreditStudy, body, 'validateCreditStudy');
  }

}

